/**
 * Devices service - handles API calls for device (POS terminal) management
 */

import { apiFetch } from "./apiClient";

const API_URL = import.meta.env.VITE_AUTH_API_URL;

export interface Device {
  id: string;
  name: string;
  username: string;
  company_id: string;
  company_name: string;
  branch_id: string;
  branch_name: string;
  last_sync: string | null;
  created_at: string;
  is_active: boolean;
  /** Stored in DB, returned on reads. */
  db_password?: string;
  /** Stored in DB, returned on reads. Used as SYNC_LOCAL_API_TOKEN / LOCAL_API_TOKEN. */
  sync_token?: string;
}

export interface DeviceCreateData {
  name: string;
  username: string;
  password: string;
  company_id: string;
  branch_id: string;
  is_active?: boolean;
}

export interface DeviceUpdateData {
  name?: string;
  password?: string;
  branch_id?: string;
  is_active?: boolean;
}

export async function getDevices(companyId?: string, branchId?: string): Promise<Device[]> {
  const params = new URLSearchParams();
  if (companyId) params.set('company_id', companyId);
  if (branchId) params.set('branch_id', branchId);

  const url = `${API_URL}/devices${params.toString() ? '?' + params.toString() : ''}`;

  const response = await apiFetch(url);

  if (!response.ok) throw new Error('Error al obtener dispositivos');

  const data = await response.json();
  return data.data || [];
}

export async function createDevice(deviceData: DeviceCreateData): Promise<Device> {
  const response = await apiFetch(`${API_URL}/devices`, {
    method: 'POST',
    body: JSON.stringify(deviceData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || errorData.message || 'Error al crear el dispositivo');
  }

  const data = await response.json();
  return data.data;
}

export async function updateDevice(id: string, deviceData: DeviceUpdateData): Promise<Device> {
  const response = await apiFetch(`${API_URL}/devices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(deviceData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || errorData.message || 'Error al actualizar el dispositivo');
  }

  const data = await response.json();
  return data.data;
}

export async function deleteDevice(id: string): Promise<void> {
  const response = await apiFetch(`${API_URL}/devices/${id}`, { method: 'DELETE' });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || errorData.message || 'Error al eliminar el dispositivo');
  }
}

/**
 * Re-autentica al admin y regenera la contraseña del dispositivo.
 * Devuelve la nueva contraseña en texto plano (única exposición).
 *
 * @param id            UUID del dispositivo
 * @param adminPassword Contraseña actual del administrador logueado
 * @returns             Nueva contraseña en texto plano
 */
export async function resetDeviceCredentials(
  id: string,
  adminPassword: string,
): Promise<string> {
  const response = await apiFetch(`${API_URL}/devices/${id}/reset-credentials`, {
    method: 'POST',
    body: JSON.stringify({ admin_password: adminPassword }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || errorData.error || 'Error al regenerar credenciales');
  }

  const data = await response.json();
  return data.data.new_password as string;
}
